import Button from '@theme/components/Button/src/tsx/Button';
import type { CSSProperties, ReactNode } from 'react';
import type { Feature, FeatureCategory, FeatureEdition, GetLabel, ShowLightbox } from '../types/feature';
import KeyPoints from './KeyPoints';

const VARIANTS = {
	list: { headingTag: 'h6', primaryButton: false },
	dialog: { headingTag: 'h4', primaryButton: true },
} as const;

interface ListItemProps {
	feature: Feature;
	categories: Record<string, FeatureCategory>;
	getLabel: GetLabel;
	editions: FeatureEdition[];
	showLightbox: ShowLightbox;
	variant?: keyof typeof VARIANTS;
	headerAction?: ReactNode;
}

const getCategoryStyle = (color: string): CSSProperties & { '--color': string } => ({ '--color': color });

export default function ListItem({ feature, categories, getLabel, editions, showLightbox, variant = 'list', headerAction = null }: ListItemProps) {
	const { headingTag: HeadingTag, primaryButton } = VARIANTS[variant] || VARIANTS.list;
	const displayGallery = (feature: Feature) => {
		if (feature.images === undefined && feature.popup_video === undefined) {
			return null;
		}
		const gallery = feature?.images || [];
		const videoID = feature?.popup_video || '';
		let thumbnail = '';

		if (videoID !== '') {
			thumbnail = `https://i.ytimg.com/vi/${videoID}/hqdefault.jpg`;
			const videoUrl = `https://youtu.be/${videoID}?feature=shared&no-cookie`;

			// if video not already in gallery
			if (!gallery.find((media) => media.url === videoUrl)) {
				gallery.unshift({
					type: 'video',
					url: videoUrl,
					title: 'Video',
				});
			}
		}

		return (
			<div className="list-item--gallery">
				<div className="list-item--gallery-wrapper">
					{gallery.map((media, index) => (
						<div key={index} className="list-item--gallery-item" onClick={() => showLightbox(feature.id, gallery, index + 1)}>
							{'video' === media.type ? (
								<div className="wrapper-video">
									<video src={media.url} poster={thumbnail}></video>
								</div>
							) : (
								<div className="wrapper-image">
									<img src={media.url} alt={media.title} />
								</div>
							)}
						</div>
					))}
				</div>
			</div>
		);
	};

	const getKeyPoints = () => {
		if (!Object.keys(feature.detail_func).length) {
			return null;
		}

		return (
			<div className="list-item--key_points">
				<div className="list-item--sub-headline">{getLabel('key_points_headline')}</div>
				<KeyPoints key={feature.id} items={feature.detail_func} getLabel={getLabel} />
			</div>
		);
	};

	const headline = <HeadingTag className="list-item--headline" dangerouslySetInnerHTML={{ __html: feature.name }}></HeadingTag>;

	return (
		<div className={`list-item-wrapper list-item-wrapper--${variant} list-item-wrapper--${feature.images || feature.popup_video ? 'media' : 'no-media'}`}>
			{headerAction ? (
				<div className="list-item--header">
					{headline}
					{headerAction}
				</div>
			) : (
				headline
			)}

			{displayGallery(feature)}

			<div className="list-item--description">
				<div className="list-item--sub-headline">{getLabel('description_headline')}</div>
				<div dangerouslySetInnerHTML={{ __html: feature.desc_list }}></div>
			</div>

			{editions.length ? (
				<div className="list-item--editions">
					<div className="list-item--sub-headline">{getLabel('edition_headline')}</div>
					<ul className="no-list">
						{editions.map((edition) => (
							<li key={edition.slug} className="list-item--icon" data-checked={feature.editions.includes(edition.slug)}>
								{edition.name}
							</li>
						))}
					</ul>
				</div>
			) : null}

			{getKeyPoints()}

			{feature.primary_category && (
				<div className="list-item--category" style={getCategoryStyle(categories[feature.primary_category].color)}>
					{categories[feature.primary_category].title}
				</div>
			)}

			{feature.content_url && (
				<div className="list-item--actions">
					<Button primary={primaryButton} href={feature.content_url}>
						{getLabel('read_more')}
					</Button>
				</div>
			)}
		</div>
	);
}
